import React, { useState, useRef } from "react";
import axios from "axios";
import { CalendarCheck, Phone, MapPin, Clock, CheckCircle2, Camera, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BUSINESS, SERVICE_OPTIONS } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  vehicle: "",
  service: "",
  preferredDate: "",
  message: "",
};

const MAX_PHOTOS = 5;

// Compress image client-side (max 1600px, JPEG) to keep uploads small & fast
const compressImage = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const maxDim = 1600;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob ? new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" }) : file);
        },
        "image/jpeg",
        0.8
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // fall back to original if it can't be read as an image
    };
    img.src = url;
  });

const Appointment = () => {
  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]); // [{file, preview}]
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handlePhotoSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file
    if (selected.length === 0) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed.`);
      return;
    }
    const toAdd = selected.slice(0, room);
    if (selected.length > room) {
      toast.error(`Only ${MAX_PHOTOS} photos allowed — added the first ${room}.`);
    }
    const nonImages = toAdd.filter((f) => !f.type.startsWith("image/"));
    if (nonImages.length > 0) {
      toast.error("Please select image files only (JPG, PNG, etc.).");
      return;
    }
    const compressed = await Promise.all(toAdd.map((f) => compressImage(f)));
    setPhotos((prev) => [
      ...prev,
      ...compressed.map((file) => ({ file, preview: URL.createObjectURL(file) })),
    ]);
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please fill in your name, phone number, and select a service.");
      return;
    }
    setSubmitting(true);
    try {
      let photoIds = [];
      if (photos.length > 0) {
        const fd = new FormData();
        photos.forEach((p) => fd.append("files", p.file));
        const uploadRes = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        photoIds = uploadRes.data.photoIds || [];
      }
      await axios.post(`${API}/appointments`, { ...form, photos: photoIds });
      setSubmitted(true);
      toast.success("Appointment request received! We'll contact you shortly.");
    } catch (err) {
      console.error("Failed to submit appointment:", err);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="appointment-page">
      <section className="bg-zinc-950 py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-red-600/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-red-500 mb-4">
            <span className="w-8 h-[2px] bg-red-500 inline-block" /> Book Online
          </span>
          <h1 className="font-heading text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-5">
            Make An <span className="text-red-500">Appointment</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed">
            Tell us about your vehicle and we'll get back to you fast with a free estimate.
          </p>
        </div>
      </section>

      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          {/* Form */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-zinc-100 shadow-sm p-8 sm:p-10">
            {submitted ? (
              <div className="text-center py-16" data-testid="appointment-success">
                <div className="w-20 h-20 mx-auto rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-6">
                  <CheckCircle2 size={38} />
                </div>
                <h2 className="font-heading text-3xl font-extrabold text-zinc-900 mb-3">Request Received!</h2>
                <p className="text-zinc-600 max-w-md mx-auto mb-8">
                  Thank you, {form.name.split(" ")[0]}! We'll reach out at {form.phone} shortly to confirm your appointment.
                </p>
                <Button onClick={() => { setForm(initialForm); setPhotos([]); setSubmitted(false); }} variant="outline" className="border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-semibold uppercase tracking-wide text-xs h-11 px-6" data-testid="appointment-book-another">
                  Book Another
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="appointment-form">
                <div className="flex items-center gap-3 mb-2">
                  <CalendarCheck size={22} className="text-red-600" />
                  <h2 className="font-heading text-2xl font-bold text-zinc-900">Appointment Details</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Full Name *</Label>
                    <Input id="name" data-testid="appointment-name-input" placeholder="John Smith" value={form.name} onChange={update("name")} className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Phone Number *</Label>
                    <Input id="phone" data-testid="appointment-phone-input" placeholder="(330) 555-0123" value={form.phone} onChange={update("phone")} className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Email</Label>
                    <Input id="email" type="email" data-testid="appointment-email-input" placeholder="you@email.com" value={form.email} onChange={update("email")} className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicle" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Vehicle (Year / Make / Model)</Label>
                    <Input id="vehicle" data-testid="appointment-vehicle-input" placeholder="2021 Honda Accord" value={form.vehicle} onChange={update("vehicle")} className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Service Needed *</Label>
                    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                      <SelectTrigger className="h-12" data-testid="appointment-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preferredDate" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Preferred Date</Label>
                    <Input id="preferredDate" type="date" data-testid="appointment-date-input" value={form.preferredDate} onChange={update("preferredDate")} className="h-12" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Tell Us About The Damage / Issue</Label>
                  <Textarea id="message" data-testid="appointment-message-input" placeholder="Describe what happened or what you need..." rows={5} value={form.message} onChange={update("message")} />
                </div>

                {/* Photo upload */}
                <div className="space-y-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                    Photos Of The Damage <span className="text-zinc-400 font-medium normal-case tracking-normal">(optional, up to {MAX_PHOTOS})</span>
                  </Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoSelect}
                    data-testid="appointment-photo-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photos.length >= MAX_PHOTOS}
                    data-testid="appointment-add-photos-btn"
                    className="w-full border-2 border-dashed border-zinc-200 hover:border-red-400 rounded-xl py-6 flex flex-col items-center gap-2 text-zinc-500 hover:text-red-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Camera size={26} />
                    <span className="text-sm font-semibold">
                      {photos.length >= MAX_PHOTOS ? "Photo limit reached" : "Tap to add photos of your car"}
                    </span>
                    <span className="text-xs text-zinc-400">Helps us give you a faster, more accurate estimate</span>
                  </button>
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-3" data-testid="appointment-photo-previews">
                      {photos.map((p, idx) => (
                        <div key={p.preview} className="relative w-24 h-24 rounded-lg overflow-hidden border border-zinc-200 group">
                          <img src={p.preview} alt={`Damage photo ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removePhoto(idx)}
                            aria-label="Remove photo"
                            data-testid={`remove-photo-${idx}`}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-zinc-900/70 hover:bg-red-600 text-white flex items-center justify-center transition-colors duration-200"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                      {photos.length < MAX_PHOTOS && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          aria-label="Add more photos"
                          className="w-24 h-24 rounded-lg border-2 border-dashed border-zinc-200 hover:border-red-400 text-zinc-400 hover:text-red-600 flex items-center justify-center transition-colors duration-200"
                        >
                          <ImagePlus size={22} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={submitting} className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide text-sm h-14 px-10 rounded-md shadow-md hover:shadow-xl transition-all duration-200" data-testid="appointment-submit-btn">
                  {submitting ? "Sending..." : "Request Appointment"}
                </Button>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-zinc-900 rounded-2xl p-8 text-white">
              <h3 className="font-heading text-xl font-bold mb-6">Prefer To Call?</h3>
              <a href={BUSINESS.phoneHref} className="flex items-center gap-3 text-lg font-bold text-red-400 hover:text-red-300 transition-colors mb-6" data-testid="appointment-sidebar-phone">
                <Phone size={20} /> {BUSINESS.phone}
              </a>
              <div className="space-y-4 text-sm text-zinc-400">
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="text-red-500 mt-0.5 shrink-0" />
                  {BUSINESS.address}
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={16} className="text-red-500 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    {BUSINESS.hours.map((h) => (
                      <div key={h.day}><span className="text-zinc-300">{h.day}:</span> {h.time}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-red-600 rounded-2xl p-8 text-white">
              <h3 className="font-heading text-xl font-bold mb-3">Need A Tow?</h3>
              <p className="text-red-100 text-sm leading-relaxed mb-5">
                We offer in-house towing. If your vehicle can't make it to us — we'll come to you.
              </p>
              <Button asChild className="bg-white text-red-700 hover:bg-zinc-100 font-bold uppercase tracking-wide text-xs h-11 px-6 w-full">
                <a href={BUSINESS.phoneHref}>Call For Towing</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Appointment;
